import React, { useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import DashboardDateFilter, {
  DEFAULT_DATE_FILTER,
} from "./DashboardDateFilter";
import DashboardExportPdfButton from "./DashboardExportPdfButton";
import RepositoryTrendSection from './RepositoryTrendSection/RepositoryTrendSection'
import AtAGlanceSection from './atAGlanceSection/AtAGlanceSection'
import KeyInsightsSection from "./keyInsightSection/KeyInsightsSection";
import AIInsightsSection from "./aiInsightsSection/AIInsightsSection";
import ProblemAreasSection from "./problemAreasSection/ProblemAreasSection";
import CoverageModuleHealthSection from "./coverageModuleHealthSection/CoverageModuleHealthSection";
import TestCycleHealthSection from "./testCycleHealthSection/TestCycleHealthSection";
import { getTestCaseSummary } from "../../modules/dashboard";
import { fetchDefectsLogged } from "../../modules/dashboard";
import QUERY_KEYS from "../../query/queryKeys";

const DashboardInsightsContent = () => {
  const { projectId } = useParams();
  const exportRef = useRef(null);
  const coverageModuleHealthRef = useRef(null);
  const wasCoverageCollapsedRef = useRef(false);
  const [dateFilter, setDateFilter] = useState(DEFAULT_DATE_FILTER);
  const [exporting, setExporting] = useState(false);


  const {
    data: testCaseSummary,
    isLoading: summaryLoading,
    isError: summaryIsError,
    error: summaryError,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: QUERY_KEYS.project.testCaseSummary(projectId, dateFilter),
    queryFn: () => getTestCaseSummary(projectId, dateFilter),
    enabled: !!projectId,
  });

    const {
    data: defectsData,
    isLoading: defectsLoading,
    isError: defectsIsError,
    error: defectsError,
  } = useQuery({
    queryKey: QUERY_KEYS.projectDashboard.defectsLogged(projectId, 'all'),
    queryFn: () => fetchDefectsLogged(projectId, null, false),
    enabled: !!projectId,
  });
  const handleBeforeExport = async () => {
    const coverageAccordion = coverageModuleHealthRef.current;
    if (coverageAccordion && !coverageAccordion.isExpanded()) {
      wasCoverageCollapsedRef.current = true;
      await coverageAccordion.expandAndWait();
    }
  };

  const handleAfterExport = () => {
    if (wasCoverageCollapsedRef.current) {
      coverageModuleHealthRef.current?.collapse();
      wasCoverageCollapsedRef.current = false;
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Dashboard Insights
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time overview of test repository health and AI-powered
            recommendations
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <DashboardDateFilter value={dateFilter} onChange={setDateFilter} />
          <DashboardExportPdfButton
            targetRef={exportRef}
            fileName="dashboard-insights"
            loading={exporting}
            setLoading={setExporting}
            onBeforeExport={handleBeforeExport}
            onAfterExport={handleAfterExport}
          />
        </Stack>
      </Stack>

      <Box ref={exportRef}>
        <Box sx={{ mb: 3 }}>
          <KeyInsightsSection
            data={testCaseSummary}
            loading={summaryLoading}
            isError={summaryIsError}
            error={summaryError}
            refetch={refetchSummary}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            gap: 3,
            mb: 3,
          }}
        >
          <Box sx={{ flexBasis: { xs: '100%', md: '75%' }, minWidth: 0 }}>
            <RepositoryTrendSection dateFilter={dateFilter} sx={{ height: '100%' }} />
          </Box>
          <Box sx={{ flexBasis: { xs: '100%', md: '25%' }, minWidth: 0 }}>
            <AtAGlanceSection
              data={testCaseSummary}
              defectsData={defectsData}
              defectsLoading={defectsLoading}
              defectsIsError={defectsIsError}
              defectsError={defectsError}
              loading={summaryLoading}
              isError={summaryIsError}
              error={summaryError}
              refetch={refetchSummary}
              sx={{ height: '100%' }}
            />
          </Box>
        </Box>
        <Box sx={{ mb: 3 }}>
          <ProblemAreasSection />
        </Box>
         <Box sx={{ mb: 3 }}>
          <TestCycleHealthSection />
        </Box>
        <Box sx={{ mb: 3 }}>
          <AIInsightsSection dateFilter={dateFilter} />
        </Box>
        <Box sx={{ mb: 3 }}>
          <CoverageModuleHealthSection ref={coverageModuleHealthRef} />
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardInsightsContent;
